import * as React from "react";
import { Fragment } from "react";
import Datetime from "react-datetime";

import { RouteComponentProps, withRouter } from "react-router";
import { LoadingViewState, RouterView } from "../RouterView";
import {
  BonusStatus,
  CalculationBasis,
  CashbackConfig,
  CashbackFrequency,
  CashbackType,
  DayOfWeek,
  RewardTemplate,
  RewardType,
  GameTag,
} from "../../http/protocol";
import { Form, FormListener } from "../form/Form";
import { getContainer } from "../../ioc/IOCSetup";
import { FormGroup } from "../form/FormGroup";
import { FormInput } from "../form/FormInput";
import { FormSubmitError, FormSubmitSuccess } from "../form/FormFeedback";
import { Submit } from "../form/Submit";
import { Validators } from "../form/Validators";
import { SettingsService } from "../../services/SettingsService";
import { SelectInput } from "../form/SelectInput";
import { CashbackConfigService } from "../../services/CashbackConfigService";
import { RewardsConfigService } from "../../services/RewardsConfigService";
import { CasinoGameTagService } from "../../services/CasinoGameTagService";
import { SecuritySettingsService } from "../../services/SecuritySettingsService";

import { gameTagsFromString, gameTagsToString } from "./utils/GameTagUtils";
import { UserLevelsEditor } from "./components/UserLevelsEditor";
import { GameTagsSection } from "./components/GameTagsSection";
import { t } from "../../i18n";
import {
  blockedFractionMessage,
  blockingFractionLimit,
  CashbackFractionThresholds,
  isLargeFraction,
  isNewlyBlockingFraction,
  largeFractionThreshold,
} from "./cashbackFractionThresholds";
import { LargeFractionConfirmationDialog, LargeFractionEntry } from "../dialog/LargeFractionConfirmationDialog";
import { EXAMPLE_WAGER, formatEuro } from "./cashbackFractionExample";
import "./styles/CashbackConfig.scss";

interface State extends LoadingViewState {
  cashbackConfig?: CashbackConfig;
  validationMap?: Record<string, string>;
  levels: string[];
  addLevelFraction: string;
  rewards: RewardTemplate[];
  gameTags: GameTag[];
  selectedGameTags: GameTag[];
  showFractionDialog: boolean;
  fractionEntries: LargeFractionEntry[];
  userCancelledSave: boolean;
  // Operator-configured warning limits (Admin Security), loaded with the config. Undefined until
  // loaded (or on fetch failure) — the advisory warning stays hidden.
  cashbackFractionThresholds?: CashbackFractionThresholds;
  // Fractions as first loaded, so the blocking ceiling fires only on a value changed this session:
  // an already-stored over-ceiling fraction is grandfathered and still saves untouched.
  initialDefaultFraction?: number;
  initialLevels: string[];
  // Which fractions a blocked save attempt flagged, so the hard-stop message renders next to them.
  saveBlockedDefault: boolean;
  blockedLevelIndices: number[];
}

interface RouteParams {
  id: string;
}

export class EditCashbackConfig extends RouterView<RouteComponentProps<RouteParams>, State> implements FormListener<CashbackConfig> {
  private readonly service: CashbackConfigService;
  private readonly settingsService: SettingsService;
  private readonly rewardService: RewardsConfigService;
  private readonly gameTagService: CasinoGameTagService;
  private readonly securitySettingsService: SecuritySettingsService;

  constructor(props: RouteComponentProps<RouteParams>) {
    super(props);
    this.service = getContainer().getCashbackConfigService();
    this.settingsService = getContainer().getSettingsService();
    this.gameTagService = getContainer().getCasinoGameTagService();
    this.rewardService = getContainer().getRewardsConfigService();
    this.securitySettingsService = getContainer().getSecuritySettingsService();

    this.state = {
      cashbackConfig: { status: BonusStatus.DISABLED },
      validationMap: {},
      levels: [],
      addLevelFraction: "",
      rewards: [],
      gameTags: [],
      selectedGameTags: [],
      showFractionDialog: false,
      fractionEntries: [],
      userCancelledSave: false,
      initialLevels: [],
      saveBlockedDefault: false,
      blockedLevelIndices: [],
    };
  }

  // Resolved when the operator confirms the large-fraction dialog, rejected when they cancel; lets
  // onSubmit await the decision inline rather than splitting the save across callbacks.
  private pendingFractionConfirmation: { resolve: () => void; reject: (reason: Error) => void } | null = null;

  getTitle(): string {
    return "Edit Cashback";
  }

  componentDidUpdate(prevProps: RouteComponentProps<RouteParams>, prevState: State) {
    if (prevProps.match.params.id !== this.props.match.params.id) {
      this.refreshContent();
    }
  }

  private updateLevelFraction = (fraction: string): void => {
    this.setState({ addLevelFraction: fraction });
  };

  private updateLevel = (fraction: string, index: number): void => {
    if (this.state.levels) {
      const levels = [...this.state.levels];
      levels[index] = fraction;
      this.setState({ levels });
    }
  };

  private addLevel = (): void => {
    if (!this.state.addLevelFraction.trim()) {
      return;
    }
    const levels = [...this.state.levels, this.state.addLevelFraction];
    this.setState({ levels, addLevelFraction: "" });
  };

  private removeLevel = (index: number): void => {
    const levels = [...this.state.levels];
    if (levels.length > index) {
      levels.splice(index, 1);
      // Keep the grandfather baselines index-aligned with the rows: dropping a row shifts every
      // later row down, so without removing the matching baseline an untouched over-ceiling level
      // would be compared against the wrong (or a now-absent) baseline and falsely block a save.
      const initialLevels = [...this.state.initialLevels];
      if (index < initialLevels.length) {
        initialLevels.splice(index, 1);
      }
      this.setState({ levels, initialLevels });
    }
  };

  private isValidLevels(levels: string[]): boolean {
    if (!levels || levels.length === 0) {
      return true; // Empty levels are valid
    }
    return levels.every((level) => !isNaN(parseFloat(level)) && isFinite(parseFloat(level)));
  }

  private convertLevelsToAttributes(levels: string[]): Record<string, number> {
    const attributes: Record<string, number> = {};
    levels.forEach((value, index) => {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        attributes[index.toString()] = numValue;
      }
    });
    return attributes;
  }

  private convertAttributesToLevels(attributes?: Record<string, number>): string[] {
    if (!attributes) return [];
    return Object.keys(attributes)
      .sort((a, b) => parseInt(a) - parseInt(b))
      .map((key) => attributes[key].toString());
  }

  loadContent(): Promise<void> {
    const isNewConfig = this.props.match.params.id === "new";
    const loadConfig = isNewConfig ? this.loadNewConfig() : this.loadExistingConfig();
    return Promise.all([loadConfig, this.loadFractionThresholds()]).then(() => undefined);
  }

  private async loadFractionThresholds(): Promise<void> {
    try {
      const settings = await this.securitySettingsService.fetchUiSettings();
      this.setState({
        cashbackFractionThresholds: {
          rtp: settings.largeCashbackFractionRtp,
          unweighted: settings.largeCashbackFractionUnweighted,
          blocking: settings.maxCashbackFraction,
          rakeback: {
            rtp: settings.largeRakebackFractionRtp,
            unweighted: settings.largeRakebackFractionUnweighted,
            blocking: settings.maxRakebackFraction,
          },
        },
      });
    } catch (error) {
      // Advisory-only: leave thresholds unset so no large-fraction warning shows.
      console.error("Error loading cashback fraction warning limits:", error);
    }
  }

  private async loadNewConfig(): Promise<void> {
    try {
      const [rewardsResponse, gameTagsResponse] = await Promise.all([
        this.rewardService.list({ limit: 1000, offset: 0, orderBy: "name", rewardType: RewardType.cash }),
        this.gameTagService.list({ limit: 1000, offset: 0, orderBy: "name" }),
      ]);

      this.setState({
        rewards: rewardsResponse.items ?? [],
        gameTags: gameTagsResponse.items ?? [],
        selectedGameTags: [],
      });
    } catch (error) {
      console.error("Error loading new config data:", error);
    }
  }

  private async loadExistingConfig(): Promise<void> {
    try {
      const cashbackConfig = await this.service.getEntity(this.props.match.params.id);
      const [rewardsResponse, gameTagsResponse] = await Promise.all([
        this.rewardService.list({ limit: 1000, offset: 0, orderBy: "name", rewardType: RewardType.cash }),
        this.gameTagService.list({ limit: 1000, offset: 0, orderBy: "name" }),
      ]);

      const selectedGameTags = gameTagsFromString(cashbackConfig.gameTags || "", gameTagsResponse.items ?? []);

      const levels = this.convertAttributesToLevels(cashbackConfig.levels);
      this.setState({
        rewards: rewardsResponse.items ?? [],
        cashbackConfig,
        levels,
        initialDefaultFraction: cashbackConfig.defaultAwardFraction,
        initialLevels: levels,
        gameTags: gameTagsResponse.items ?? [],
        selectedGameTags,
      });
    } catch (error) {
      console.error("Error loading existing config:", error);
    }
  }

  // v1 has no calculation basis, so every fraction is judged against the UNWEIGHTED threshold — the
  // same basis the inline warning uses. The default fraction and each level (index = player level).
  private collectLargeFractions(): LargeFractionEntry[] {
    const entries: LargeFractionEntry[] = [];
    const thresholds = this.state.cashbackFractionThresholds;
    const type = this.state.cashbackConfig?.type;
    const defaultFraction = Number(this.state.cashbackConfig?.defaultAwardFraction);
    if (isLargeFraction(defaultFraction, CalculationBasis.UNWEIGHTED, thresholds, type)) {
      entries.push({ label: "Default Award Fraction", fraction: defaultFraction });
    }
    this.state.levels.forEach((level, index) => {
      const fraction = Number(level);
      if (isLargeFraction(fraction, CalculationBasis.UNWEIGHTED, thresholds, type)) {
        entries.push({ label: `Level ${index}`, fraction });
      }
    });
    return entries;
  }

  // The blocking ceiling only stops a fraction changed this session — a value equal to its
  // originally-loaded string is grandfathered even when it is over the ceiling. A level index with no
  // baseline (a row added this session) has no loaded value, so any over-ceiling entry there blocks.
  private collectBlockedFractions(): { blockedDefault: boolean; blockedLevelIndices: number[] } {
    const thresholds = this.state.cashbackFractionThresholds;
    const type = this.state.cashbackConfig?.type;
    const blockedDefault = isNewlyBlockingFraction(this.state.cashbackConfig?.defaultAwardFraction, this.state.initialDefaultFraction, thresholds, type);

    const blockedLevelIndices: number[] = [];
    this.state.levels.forEach((level, index) => {
      const baseline = index < this.state.initialLevels.length ? this.state.initialLevels[index] : undefined;
      if (isNewlyBlockingFraction(level, baseline, thresholds, type)) {
        blockedLevelIndices.push(index);
      }
    });
    return { blockedDefault, blockedLevelIndices };
  }

  async onSubmit(): Promise<void> {
    if (!this.state.cashbackConfig) {
      throw new Error("No cashback config available");
    }

    if (!this.isValidLevels(this.state.levels)) {
      throw new Error("Level configuration is not valid");
    }

    this.setState({ userCancelledSave: false });

    const { blockedDefault, blockedLevelIndices } = this.collectBlockedFractions();
    this.setState({ saveBlockedDefault: blockedDefault, blockedLevelIndices });
    if (blockedDefault || blockedLevelIndices.length > 0) {
      throw new Error(
        blockedFractionMessage(
          "cashback.config.large-fraction-blocked",
          blockingFractionLimit(this.state.cashbackFractionThresholds, this.state.cashbackConfig?.type),
        ),
      );
    }

    const largeFractions = this.collectLargeFractions();
    if (largeFractions.length > 0) {
      this.setState({ fractionEntries: largeFractions, showFractionDialog: true });
      try {
        await new Promise<void>((resolve, reject) => {
          this.pendingFractionConfirmation = { resolve, reject };
        });
      } catch (error) {
        // Cancelling is a normal choice, not a failure — suppress the form's error/success banners.
        this.setState({ userCancelledSave: true });
        throw error;
      }
    }

    const levelsAttributes = this.convertLevelsToAttributes(this.state.levels);
    const gameTagsString = gameTagsToString(this.state.selectedGameTags);

    const configToSave: CashbackConfig = {
      ...this.state.cashbackConfig,
      levels: levelsAttributes,
      gameTags: gameTagsString,
    };

    const savedConfig = await this.service.save(configToSave);
    const savedLevels = this.convertAttributesToLevels(savedConfig.levels);
    this.setState({
      cashbackConfig: savedConfig,
      levels: savedLevels,
      initialDefaultFraction: savedConfig.defaultAwardFraction,
      initialLevels: savedLevels,
    });
  }

  private handleFractionConfirm = (): void => {
    this.setState({ showFractionDialog: false });
    this.pendingFractionConfirmation?.resolve();
    this.pendingFractionConfirmation = null;
  };

  private handleFractionCancel = (): void => {
    this.setState({ showFractionDialog: false });
    this.pendingFractionConfirmation?.reject(new Error("USER_CANCELLED"));
    this.pendingFractionConfirmation = null;
  };

  formDidUpdate(formModel: CashbackConfig, validationMap?: Record<string, string>): void {
    this.setState({ cashbackConfig: formModel, validationMap });
  }

  private handleModeChange = (excluded: boolean): void => {
    this.setState((prevState) => ({
      cashbackConfig: {
        ...prevState.cashbackConfig,
        excluded,
      },
    }));
  };

  private handleGameTagsChange = (selectedTags: GameTag[]): void => {
    this.setState({ selectedGameTags: selectedTags });
  };

  private removeAttribute(key: string): void {
    if (this.state.cashbackConfig?.levels && this.state.cashbackConfig.levels[key]) {
      const levels = { ...this.state.cashbackConfig.levels };
      delete levels[key];
      this.setState((prevState) => ({
        cashbackConfig: {
          ...prevState.cashbackConfig,
          levels,
        },
      }));
    }
  }

  private addAttribute(key: string, value: number): void {
    const levels = this.state.cashbackConfig?.levels ? { ...this.state.cashbackConfig.levels } : {};
    levels[key] = value;
    this.setState((prevState) => ({
      cashbackConfig: {
        ...prevState.cashbackConfig,
        levels,
      },
    }));
  }

  renderContent(): JSX.Element {
    if (!this.state.cashbackConfig) {
      return <span>Loading...</span>;
    }

    const isNewConfig = this.props.match.params.id === "new";
    // A cancelled confirmation or a save blocked on an over-ceiling fraction is a deliberate outcome,
    // not a form error, so the generic submit-error banner stays hidden in those cases.
    const showSubmitError = !this.state.userCancelledSave && !this.state.saveBlockedDefault && this.state.blockedLevelIndices.length === 0;

    return (
      <div>
        <div className="wrapper wrapper-content animated fadeInRight">
          <Form
            className="form form-horizontal"
            validationMap={this.state.validationMap}
            modelObject={this.state.cashbackConfig}
            formListener={this}
          >
            <div className="row">
              <div className="col-lg-12">
                {this.renderGeneralSettings(isNewConfig)}
                {this.renderSchedulingSection()}
                <UserLevelsEditor
                  levels={this.state.levels}
                  addLevelFraction={this.state.addLevelFraction}
                  thresholds={this.state.cashbackFractionThresholds}
                  rewardType={this.state.cashbackConfig?.type}
                  blockedLevelIndices={this.state.blockedLevelIndices}
                  onLevelChange={this.updateLevel}
                  onAddLevel={this.addLevel}
                  onRemoveLevel={this.removeLevel}
                  onAddLevelFractionChange={this.updateLevelFraction}
                />
                <GameTagsSection
                  cashbackConfig={this.state.cashbackConfig}
                  gameTags={this.state.gameTags}
                  selectedGameTags={this.state.selectedGameTags}
                  onModeChange={this.handleModeChange}
                  onGameTagsChange={this.handleGameTagsChange}
                  loading={this.state.loading}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-lg-12">
                <div className="card-box">
                  {showSubmitError && <FormSubmitError />}
                  {!this.state.userCancelledSave && <FormSubmitSuccess text="Cashback was updated successfully" />}
                  <div className="form-group">
                    <div className="col-sm-12">
                      <Submit className="btn btn-primary">Save</Submit>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <LargeFractionConfirmationDialog
              show={this.state.showFractionDialog}
              entries={this.state.fractionEntries}
              threshold={
                this.state.cashbackFractionThresholds
                  ? largeFractionThreshold(CalculationBasis.UNWEIGHTED, this.state.cashbackFractionThresholds, this.state.cashbackConfig?.type)
                  : 0
              }
              onConfirm={this.handleFractionConfirm}
              onCancel={this.handleFractionCancel}
            />
          </Form>
        </div>
      </div>
    );
  }

  // Advisory only: a fraction at or above the single V1 (unweighted) threshold can pay out far
  // more than intended, but saving is never blocked.
  private renderLargeFractionWarning(): JSX.Element | null {
    const thresholds = this.state.cashbackFractionThresholds;
    const type = this.state.cashbackConfig?.type;
    const fraction = Number(this.state.cashbackConfig?.defaultAwardFraction);
    if (!thresholds || !isLargeFraction(fraction, CalculationBasis.UNWEIGHTED, thresholds, type)) {
      return null;
    }

    return (
      <FormGroup className="form-group">
        <div className="col-sm-10 col-sm-offset-4">
          <div
            className="alert alert-warning"
            data-testid="cashback-v1-large-fraction-warning"
          >
            <strong>{t("cashback.config.large-fraction-warning-title", undefined, "Unusually large fraction")}</strong>{" "}
            {t(
              "cashback.config.large-fraction-warning",
              { threshold: largeFractionThreshold(CalculationBasis.UNWEIGHTED, thresholds, type) },
              "A fraction of {{threshold}} or more is unusually large and may pay out far more than intended. This is advisory only — you can still save.",
            )}
          </div>
        </div>
      </FormGroup>
    );
  }

  // Hard stop (not advisory): shown only after a save attempt that a changed, over-ceiling default
  // fraction rejected. Rendered where the advisory warning sits so the operator reads both together.
  private renderLargeFractionBlock(): JSX.Element | null {
    if (!this.state.saveBlockedDefault) {
      return null;
    }

    return (
      <FormGroup className="form-group">
        <div className="col-sm-10 col-sm-offset-4">
          <div
            className="alert alert-danger"
            data-testid="cashback-v1-large-fraction-blocked"
          >
            {blockedFractionMessage(
              "cashback.config.large-fraction-blocked",
              blockingFractionLimit(this.state.cashbackFractionThresholds, this.state.cashbackConfig?.type),
            )}
          </div>
        </div>
      </FormGroup>
    );
  }

  // Worked example that recomputes as the fraction is typed. V1 has a single (unweighted) basis, so the
  // fraction always multiplies the full wagered/lost amount — no basis branch as on the V2 form.
  private renderFractionInfoCard(): JSX.Element {
    const fraction = Number(this.state.cashbackConfig?.defaultAwardFraction);
    const safeFraction = Number.isFinite(fraction) ? fraction : 0;
    const payout = EXAMPLE_WAGER * safeFraction;

    return (
      <FormGroup className="form-group">
        <div className="col-sm-10 col-sm-offset-4">
          <div
            className="alert alert-info"
            data-testid="cashback-v1-fraction-info"
          >
            <strong>{t("cashback.config.fraction-info-title", undefined, "How the fraction applies")}</strong>
            <p className="mt-2 mb-1">
              {t(
                "cashback.config.fraction-info-intro",
                undefined,
                'The fraction multiplies the player\'s contribution for the period. "Contribution" depends on type:',
              )}
            </p>
            <ul className="mb-2">
              <li>
                <strong>RAKEBACK:</strong> {t("cashback.config.fraction-info-rakeback", undefined, "sum of wagers")}
              </li>
              <li>
                <strong>CASHBACK:</strong> {t("cashback.config.fraction-info-cashback", undefined, "net loss (wagers − wins)")}
              </li>
            </ul>
            <p className="mb-1">
              {t("cashback.config.fraction-info-example-intro", { wager: EXAMPLE_WAGER }, "For €{{wager}} wagered and lost, with the fraction entered above:")}
            </p>
            <ul
              className="mb-2"
              data-testid="cashback-v1-fraction-example"
            >
              <li>
                &euro;{EXAMPLE_WAGER} &times; {safeFraction} = <strong>&euro;{formatEuro(payout)}</strong>
              </li>
            </ul>
            <p className="mb-0">
              {t(
                "cashback.config.fraction-info-warning",
                undefined,
                "The fraction multiplies the full wagered or lost amount, so keep it small — 0.10 on €100 already pays out €10.",
              )}
            </p>
          </div>
        </div>
      </FormGroup>
    );
  }

  private renderGeneralSettings(isNewConfig: boolean): JSX.Element {
    return (
      <div className="card-box">
        <div className="ibox-content">
          <h4 className="ibox-title">General Settings</h4>
          {!isNewConfig && <div className="hr-line-dashed"></div>}

          {!isNewConfig && (
            <FormGroup className="form-group">
              <label className="col-sm-2 control-label">ID</label>
              <div className="col-sm-10">
                <FormInput
                  model="id"
                  type="integer"
                  className="form-control"
                  disabled={true}
                />
              </div>
            </FormGroup>
          )}

          <div className="hr-line-dashed"></div>
          <FormGroup className="form-group">
            <label className="col-sm-2 control-label">Name</label>
            <div className="col-sm-10">
              <FormInput
                model="name"
                validators={[Validators.required()]}
                type="text"
                className="form-control"
              />
              <span className="help">Internal name, will not be shown to players</span>
            </div>
          </FormGroup>

          <FormGroup className="form-group">
            <label className="col-sm-2 control-label">Status</label>
            <div className="col-sm-10">
              <SelectInput
                model="status"
                values={Object.keys(BonusStatus)}
                className="form-control"
              />
              <span className="help">Status of the cashback. If ENABLED then it will run and payout as configured in scheduling.</span>
            </div>
          </FormGroup>

          <FormGroup className="form-group">
            <label className="col-sm-2 control-label">Type</label>
            <div className="col-sm-10">
              <SelectInput
                model="type"
                values={Object.keys(CashbackType)}
                validators={[Validators.required()]}
                className="form-control"
              />
              <span className="help">Cashback = based on total lost amount, Rakeback = based on wagered amount</span>
            </div>
          </FormGroup>

          <FormGroup className="form-group">
            <label className="col-sm-4 control-label">Default Award Fraction</label>
            <div className="col-sm-10">
              <FormInput
                model="defaultAwardFraction"
                validators={[Validators.required(), Validators.number()]}
                type="float"
                className="form-control"
                data-testid="cashback-v1-default-fraction"
              />
              <span className="help">
                The default amount to apply. 0 if none. This fraction will be multiplied with losses or wagered amount, according to type, and awarded to the
                player as a cash reward. Example: 0.10 = 10%
              </span>
            </div>
          </FormGroup>

          {this.renderLargeFractionWarning()}
          {this.renderLargeFractionBlock()}
          {this.renderFractionInfoCard()}

          <FormGroup className="form-group">
            <label className="col-sm-2 control-label">Lifetime Loss Only</label>
            <div className="col-sm-10">
              <FormInput
                model="lifetimeLossOnly"
                type="checkbox"
              />
              <span className="help">&nbsp; Only players that have more life time losses than winnings should be eligible for the Cashback</span>
            </div>
          </FormGroup>

          <FormGroup className="form-group">
            <label className="col-sm-2 control-label">Products</label>
            <div className="col-sm-10">
              <FormInput
                validators={[Validators.required()]}
                model="products"
                type="text"
                className="form-control"
              />
              <span className="help">Comma separated list of products to be included in the cashback calculations</span>
            </div>
          </FormGroup>

          <FormGroup className="form-group">
            <label className="col-sm-4 control-label">Cash Reward Template ID</label>
            <div className="col-sm-10">
              <SelectInput
                className="form-control"
                model="cashRewardId"
                optionModelValue={(m) => m.id}
                values={this.state.rewards}
                displayValue={(m) => m.name}
              />
              <span className="help">
                You must specify a cash reward template that will be used for payouts. Currency does not have to match, it will be overridden by the payout.
              </span>
            </div>
          </FormGroup>
        </div>
      </div>
    );
  }

  private renderSchedulingSection(): JSX.Element {
    const { cashbackConfig } = this.state;

    return (
      <div className="card-box">
        <div className="ibox-content">
          <h4 className="ibox-title">Scheduling</h4>
          <FormGroup className="form-group">
            <label className="col-sm-2 control-label">Recurrence</label>
            <div className="col-sm-10">
              <SelectInput
                model="frequency"
                values={Object.keys(CashbackFrequency)}
                validators={[Validators.required()]}
                className="form-control"
              />
              <span className="help">How often the cashback should perform a payout</span>
            </div>
          </FormGroup>

          <FormGroup className="form-group">
            <label className="col-sm-2 control-label">Payout Time</label>
            <div className="col-sm-10">
              <FormInput
                model="payoutTime"
                validators={[Validators.required()]}
                type="text"
                className="form-control"
              />
              <span className="help">Time in UTC. Format must be HH:00, example: 09:00 or 18:00</span>
              <br />
              <span className="help">
                <strong>
                  Cashback is calculated up to 1 hour before payout. For example, if the payout is at 14:00, only events before 13:00 are considered, and later
                  events are excluded.
                </strong>
              </span>
            </div>
          </FormGroup>

          {cashbackConfig?.frequency === CashbackFrequency.WEEKLY && (
            <FormGroup className="form-group">
              <label className="col-sm-2 control-label">Payout Day of Week</label>
              <div className="col-sm-10">
                <SelectInput
                  model="payoutDayOfWeek"
                  values={Object.keys(DayOfWeek)}
                  validators={[]}
                  className="form-control"
                />
                <span className="help">Only applies for Weekly payout</span>
              </div>
            </FormGroup>
          )}

          {cashbackConfig?.frequency === CashbackFrequency.MONTHLY && (
            <FormGroup className="form-group">
              <label className="col-sm-2 control-label">Payout Day of Month</label>
              <div className="col-sm-10">
                <FormInput
                  model="payoutDayOfMonth"
                  validators={[Validators.number()]}
                  type="integer"
                  className="form-control"
                />
                <span className="help">Only applies for Monthly payout. Must be between 1 and 28.</span>
              </div>
            </FormGroup>
          )}
        </div>
      </div>
    );
  }
}

interface DateRangeProps {
  setToDate: (d: number) => void;
  setFromDate: (d: number) => void;
  fromDate?: Date | number;
  toDate?: Date | number;
}

export class DateRange extends React.Component<DateRangeProps, { from?: any; to?: any }> {
  private to = React.createRef<HTMLDivElement>();

  constructor(props: DateRangeProps) {
    super(props);
    const fromDate = typeof this.props.fromDate === "number" ? new Date(this.props.fromDate) : this.props.fromDate;
    const toDate = typeof this.props.toDate === "number" ? new Date(this.props.toDate) : this.props.toDate;
    this.state = { from: fromDate, to: toDate };
  }

  render(): JSX.Element {
    const { from, to } = this.state;
    const modifiers = { start: from, end: to };

    return (
      <Fragment>
        <div style={{ display: "flex" }}>
          <Datetime
            onChange={(e) => this.props.setFromDate(Number(e))}
            defaultValue={new Date()}
            viewDate={new Date()}
            dateFormat="yyyy-MM-DD"
            timeFormat="HH:mm"
          />
          <Datetime
            onChange={(e) => this.props.setToDate(Number(e))}
            defaultValue={new Date()}
            viewDate={new Date()}
            dateFormat="yyyy-MM-DD"
            timeFormat="HH:mm"
          />
        </div>
      </Fragment>
    );
  }
}

export default withRouter(EditCashbackConfig);
